// src/components/onboarding/onboarding-batch-config.tsx
import React, { useEffect, useMemo, useState, type Dispatch, type SetStateAction } from "react";
import {
  Beaker, Zap, Gauge, Rocket, Sparkles, Settings2, AlertCircle, Scale, Layers,
} from "lucide-react";

import { listScorerModels, type ScorerModel } from "@/api/onboarding-scoring-api";

import {
  MEASUREMENT_MODELS,
  MEASUREMENT_LANE_OPTIONS,
  getPerCallCost,
  getModelLabel,
  resolveMeasurementVersion,
  getMeasurementVersionLabel,
  resolveMeasurementWebSearch,
  resolveMeasurementEndpoint,
  type MeasurementModel,
} from "@/app/constants/models";

// Re-export the type for backwards compatibility (callers like
// stage11-run-history import `MeasurementModel` from this file).
export type { MeasurementModel };

// Which model versions a run executes against:
//   "baseline" → the exact versions the imported baseline ran with (apples-to-
//                apples with the baseline run)
//   "default"  → the current live product versions
export type VersionMode = "baseline" | "default";

// How the Stage-5 judge calls are SENT. Same judge, same rubric, same repeats,
// same request either way — this is a cost-and-latency decision, not a
// measurement one.
//
//   "batch"  → Anthropic Message Batches: 50% of standard prices, results when
//              the batch ends (usually under an hour, guaranteed within 24).
//   "direct" → one synchronous request per response: full price, finishes in
//              minutes.
export type ScoringMode = "batch" | "direct";

// What the direct path costs relative to batch. Batched tokens bill at half,
// so direct is 2x. The server reports the same figure on the scoring state as
// `direct_mode_cost_multiplier`; this constant is only what the picker renders
// before a run exists to ask about.
export const DIRECT_SCORING_COST_MULTIPLIER = 2;

export interface BatchConfig {
  queries: number;          // How many of the framework's 25 queries to run
  reps: number;             // How many reps per query per model
  models: MeasurementModel[]; // Which providers to include
  versionMode?: VersionMode;  // Defaults to "baseline" when unset
  // Optional per-lane model overrides: { lane: version }. A lane absent here
  // runs its versionMode default. The backend re-validates each override (and
  // never allows Opus on the Claude lane), so this is a hint, not a guarantee.
  modelOverrides?: Record<string, string>;
  // Optional per-lane web-search overrides: { lane: boolean }. true = probed
  // web-grounded (search ON), false = static. A lane absent here runs its
  // catalog default (webSearchDefault). Sparse — only lanes flipped from their
  // default are recorded, so a plain run is unchanged.
  webSearchOverrides?: Record<string, boolean>;
  // Optional per-lane endpoint overrides (#90): { lane: "sonar" | "agent" }.
  // Today only meaningful for the perplexity lane (Sonar chat completions,
  // retiring 2026-09-27, vs. the Agent API). Sparse — a lane absent here (or
  // with no Agent variant at all) runs its catalog default_endpoint.
  endpointOverrides?: Record<string, string>;
  // The Stage-5 judge for this run, chosen HERE — before anything runs — and
  // honoured by the auto-pipeline that scores the run the moment collection
  // finishes. Unset = the registry default judge.
  //
  // This used to be a Stage-5 question, asked after the pipeline had already
  // scored on the default. The operator was shown a full judge picker for a
  // run that was already complete, picked a different judge, and paid for a
  // second full pass (customer 13, run 29).
  scorerModelId?: string;
  // How the Stage-5 scoring pass is SENT. Unset = the server decides, which is
  // "batch" for ordinary scoring. See ScoringModePicker below.
  scoringMode?: ScoringMode;
}

interface BatchConfigPickerProps {
  totalQueries: number;     // From framework.queries.length, capped at 25
  value: BatchConfig;
  // The customer's standing per-lane version pins (GET …/measurement/
  // version-policy). Display only: the server resolves the pins the run
  // actually uses. Passed so the per-lane rows show what WILL run — a client
  // pinned to its first read has left the catalog default behind, and a picker
  // mirroring only the catalog would name a model the run never calls.
  versionPins?: Record<string, string> | null;
  // What a lane with no pin yet runs on its first read (Global Settings →
  // Measurement defaults for new clients). Display only, like versionPins:
  // without it a new client's Perplexity row would read the catalog's "Fast"
  // while the run is pinned to "Low".
  firstReadDefaults?: Record<string, string> | null;
  // True when this customer is on the pinned-to-first-read policy (no baseline
  // of its own). Drives the "Current" blurb, so an operator can see why the
  // default moved off Baseline.
  versionPinned?: boolean;
  // Accepts an updater as well as a plain value. A handler that changes TWO
  // fields must do so from `prev`, not from the render-time `value` — two
  // back-to-back `onChange({ ...value, x })` calls both spread the same stale
  // object and the second silently drops the first's write. That is how the
  // #90 endpoint toggle shipped dead: it set `endpointOverrides`, then cleared
  // `modelOverrides` from the pre-click value, and the lane never left Sonar.
  onChange: Dispatch<SetStateAction<BatchConfig>>;
}

// ─── Cost computation ──────────────────────────────────────────────────────
// Per-call costs come from the central catalog (src/app/constants/models.ts).
// Keep them in sync with backend PER_CALL_COST_USD in onboarding_service.py.

export function computeBatchCost(c: BatchConfig): number {
  const perRunCostPerQuery = c.models.reduce(
    (sum, m) => sum + getPerCallCost(m) * c.reps,
    0,
  );
  return perRunCostPerQuery * c.queries;
}

export function computeBatchCalls(c: BatchConfig): number {
  return c.queries * c.reps * c.models.length;
}

// Some runs persist batch_config.queries as a NUMBER (e.g. 25) and others —
// notably the baseline run — as an ARRAY of query IDs (["q1",…,"q25"]). Any
// display or arithmetic that treats it as a number then breaks: it renders the
// joined array ("q1q2…q25 queries") or multiplies to NaN ("$NaN", "NaN calls").
// Normalize to a positive integer: array → length, numeric → itself, else
// fallback.
export function toCount(v: unknown, fallback: number): number {
  if (Array.isArray(v)) return v.length;
  const n = Number(v);
  return Number.isFinite(n) && n > 0 ? n : fallback;
}

// ─── Wall-clock time estimate ──────────────────────────────────────────────
// Mirrors the worker setup in backend/app/celery/celery_tasks.py and
// celery_app.py:
//   - PARALLEL_BATCH_SIZE = 16    → up to 16 LLM calls run concurrently
//   - task_soft_time_limit = 4 h  → backstop only. On hitting it the worker
//     checkpoints and AUTO-re-enqueues a continuation of the SAME run, so a
//     battery never needs a manual click to finish and no calls are lost.
//
// SECONDS_PER_BATCH is empirically calibrated against observed runs: ~85 sec
// per batch (slowest-of-N model dominates). We round up to 90 to stay
// slightly conservative.

export const PARALLEL_BATCH_SIZE = 16;
export const SECONDS_PER_BATCH = 90;
export const WORKER_SOFT_BUDGET_SECONDS = 4 * 60 * 60;

export function computeEstimatedSeconds(c: BatchConfig): number {
  const calls = computeBatchCalls(c);
  if (calls <= 0) return 0;
  return Math.ceil(calls / PARALLEL_BATCH_SIZE) * SECONDS_PER_BATCH;
}

export function formatDuration(seconds: number): string {
  if (seconds < 60) return `${Math.round(seconds)}s`;
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const remM = minutes % 60;
  return remM === 0 ? `${hours} h` : `${hours} h ${remM} min`;
}

// ─── Preset definitions ────────────────────────────────────────────────────
// Presets describe a SHAPE (queries + reps) only — they run against whichever
// models the user has selected in the always-visible Models block. This means
// clicking Quick with 4 models = 40 calls; with 2 models = 20 calls.
type PresetKey = "smoke" | "quick" | "half" | "full" | "max" | "custom";

interface PresetShape {
  queries: number;
  reps: number;
}

const PRESETS: Array<{
  key: PresetKey;
  label: string;
  blurb: string;
  icon: React.ComponentType<{ className?: string; style?: React.CSSProperties }>;
  color: string;
  shape: (totalQueries: number) => PresetShape;
}> = [
  {
    key: "smoke",
    label: "Smoke",
    blurb: "plumbing only",
    icon: Beaker,
    color: "var(--muted-foreground)",
    shape: () => ({ queries: 1, reps: 1 }),
  },
  {
    key: "quick",
    label: "Quick",
    blurb: "low reps — quick smoke",
    icon: Zap,
    color: "var(--signal-blue)",
    shape: (n) => ({ queries: Math.min(5, n), reps: 2 }),
  },
  {
    key: "half",
    label: "Half",
    blurb: "medium reps — defensible draft",
    icon: Gauge,
    color: "var(--chart-3)",
    shape: (n) => ({ queries: n, reps: 5 }),
  },
  {
    key: "full",
    label: "Full",
    blurb: "high reps — production baseline",
    icon: Rocket,
    color: "var(--alpha-teal)",
    shape: (n) => ({ queries: n, reps: 8 }),
  },
  {
    key: "max",
    label: "Max",
    blurb: "max reps — full coverage",
    icon: Sparkles,
    color: "var(--chart-2)",
    shape: (n) => ({ queries: n, reps: 10 }),
  },
];

/** Best-match preset for a given config (used to highlight the right card). */
function matchPreset(c: BatchConfig, totalQueries: number): PresetKey {
  for (const p of PRESETS) {
    const shape = p.shape(totalQueries);
    if (shape.queries === c.queries && shape.reps === c.reps) {
      return p.key;
    }
  }
  return "custom";
}

// ─── Component ─────────────────────────────────────────────────────────────
export function BatchConfigPicker({
  totalQueries,
  value,
  onChange,
  versionPins,
  firstReadDefaults,
  versionPinned,
}: BatchConfigPickerProps) {
  const matched = useMemo(() => matchPreset(value, totalQueries), [value, totalQueries]);
  const [showCustom, setShowCustom] = useState(matched === "custom");

  const handlePreset = (p: typeof PRESETS[number]) => {
    setShowCustom(false);
    const shape = p.shape(totalQueries);
    // Presets only set shape (queries + reps). Models come from whatever
    // the user has chosen in the always-visible Models block above.
    onChange({ ...value, queries: shape.queries, reps: shape.reps });
  };

  const handleCustomToggle = () => {
    setShowCustom((s) => !s);
  };

  const handleToggleModel = (m: MeasurementModel) => {
    const isOn = value.models.includes(m);
    const next = isOn
      ? value.models.filter((x) => x !== m)
      : [...value.models, m];
    // Don't allow zero models — silently keep the click.
    if (next.length === 0) return;
    onChange({ ...value, models: next });
  };

  const calls = computeBatchCalls(value);
  const cost = computeBatchCost(value);
  const modelCount = value.models.length;
  const estimatedSec = computeEstimatedSeconds(value);
  const budgetRatio = estimatedSec / WORKER_SOFT_BUDGET_SECONDS;
  const timeRisk: "ok" | "warn" | "danger" =
    budgetRatio >= 1 ? "danger" : budgetRatio >= 0.7 ? "warn" : "ok";

  return (
    <div
      className="rounded-xl"
      style={{
        backgroundColor: "var(--muted)",
        border: "1px solid var(--border)",
        padding: 12,
      }}
    >
      <div className="flex items-center justify-between mb-3">
        <div>
          <div
            style={{
              fontSize: 10,
              letterSpacing: "0.06em",
              textTransform: "uppercase",
              fontWeight: 700,
              color: "var(--muted-foreground)",
            }}
          >
            Test scope
          </div>
          <div
            style={{
              fontSize: "var(--text-xs)",
              color: "var(--muted-foreground)",
              marginTop: 2,
            }}
          >
            Pick a batch size. Start small while testing — promote up later.
          </div>
        </div>
        <div className="text-right">
          <div
            style={{
              fontSize: "var(--text-base)",
              fontWeight: 700,
              color: "var(--foreground)",
              lineHeight: 1,
            }}
          >
            ${cost.toFixed(2)}
          </div>
          <div
            style={{
              fontSize: 11,
              color: "var(--muted-foreground)",
              marginTop: 2,
            }}
          >
            {calls} call{calls === 1 ? "" : "s"} · ~{formatDuration(estimatedSec)}
          </div>
        </div>
      </div>

      {timeRisk !== "ok" && (
        <div
          className="flex items-start gap-1.5 p-2 mb-2 rounded-md"
          style={{
            backgroundColor: timeRisk === "danger"
              ? "rgba(220,38,38,0.08)"
              : "rgba(245,158,11,0.08)",
            border: `1px solid ${
              timeRisk === "danger" ? "var(--infrared-coral)" : "var(--chart-3)"
            }`,
          }}
        >
          <AlertCircle
            className="w-3 h-3 mt-0.5 flex-shrink-0"
            style={{
              color: timeRisk === "danger" ? "var(--infrared-coral)" : "var(--chart-3)",
            }}
          />
          <span style={{ fontSize: 11, color: "var(--foreground)", lineHeight: 1.4 }}>
            {timeRisk === "danger" ? (
              <>
                <strong>Estimated ~{formatDuration(estimatedSec)}</strong> — longer
                than the worker's {formatDuration(WORKER_SOFT_BUDGET_SECONDS)}{" "}
                budget. The run won't be lost: it checkpoints and{" "}
                <strong>continues automatically</strong> in another worker pass
                until it finishes — no clicking required. Shrink the scope only
                if you'd rather it complete in a single pass.
              </>
            ) : (
              <>
                <strong>Estimated ~{formatDuration(estimatedSec)}</strong> —
                approaching the worker's {formatDuration(WORKER_SOFT_BUDGET_SECONDS)}{" "}
                budget. If it runs over, partial results are preserved and the
                run <strong>continues automatically</strong> — no action needed.
              </>
            )}
          </span>
        </div>
      )}

      {/* Models — always visible. Presets run against this selection. */}
      <div
        className="rounded-lg p-3 mb-2"
        style={{
          backgroundColor: "var(--card)",
          border: "1px solid var(--border)",
        }}
      >
        <div className="flex items-center justify-between mb-1.5">
          <span
            style={{
              fontSize: 11,
              fontWeight: 600,
              color: "var(--foreground)",
            }}
          >
            Models
          </span>
          <span style={{ fontSize: 10, color: "var(--muted-foreground)" }}>
            {modelCount} of {MEASUREMENT_MODELS.length} selected · presets run against these
          </span>
        </div>
        <div className="flex flex-wrap gap-1.5">
          {MEASUREMENT_MODELS.map((m) => {
            const isOn = value.models.includes(m);
            return (
              <button
                key={m}
                onClick={() => handleToggleModel(m)}
                className="px-2.5 py-1 rounded-md transition-all"
                style={{
                  backgroundColor: isOn
                    ? "var(--alpha-teal-10)"
                    : "var(--muted)",
                  border: `1px solid ${
                    isOn ? "var(--alpha-teal)" : "var(--border)"
                  }`,
                  color: isOn ? "var(--alpha-teal)" : "var(--muted-foreground)",
                  fontSize: 11,
                  fontWeight: 600,
                  cursor: "pointer",
                }}
              >
                {getModelLabel(m)}
              </button>
            );
          })}
        </div>
      </div>

      {/* Model versions — which versions the run executes against. */}
      <VersionModePicker
        value={value.versionMode ?? "baseline"}
        onChange={(versionMode) => onChange({ ...value, versionMode })}
        pinned={!!versionPinned}
        hasPins={Object.keys(versionPins ?? {}).length > 0}
      />

      {/* Per-lane model + grounding — shows the exact model each selected
          provider runs and whether it's probed web-grounded or static, and lets
          the admin override both (model guarded: Claude is Sonnet-only). */}
      <LaneModelPicker
        models={value.models}
        mode={value.versionMode ?? "baseline"}
        // A real pin always beats the new-client default (first-seen wins).
        pins={{ ...(firstReadDefaults ?? {}), ...(versionPins ?? {}) }}
        overrides={value.modelOverrides ?? {}}
        onChange={(modelOverrides) => onChange((prev) => ({ ...prev, modelOverrides }))}
        webSearchOverrides={value.webSearchOverrides ?? {}}
        onWebSearchChange={(webSearchOverrides) => onChange((prev) => ({ ...prev, webSearchOverrides }))}
        endpointOverrides={value.endpointOverrides ?? {}}
        onEndpointChange={(endpointOverrides) => onChange((prev) => ({ ...prev, endpointOverrides }))}
      />

      {/* Preset grid — 5 across on desktop, 2-wide on mobile */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
        {PRESETS.map((p) => {
          const isSelected = matched === p.key && !showCustom;
          const Icon = p.icon;
          const shape = p.shape(totalQueries);
          const presetCalls = shape.queries * shape.reps * modelCount;
          return (
            <button
              key={p.key}
              onClick={() => handlePreset(p)}
              className="text-left rounded-lg p-2.5 transition-all"
              style={{
                backgroundColor: isSelected
                  ? "var(--alpha-teal-10)"
                  : "var(--card)",
                border: `1px solid ${
                  isSelected ? "var(--alpha-teal)" : "var(--border)"
                }`,
                cursor: "pointer",
              }}
            >
              <div className="flex items-center gap-1.5 mb-1">
                <Icon className="w-3.5 h-3.5" style={{ color: p.color }} />
                <span
                  style={{
                    fontSize: 12,
                    fontWeight: 700,
                    color: isSelected ? "var(--alpha-teal)" : "var(--foreground)",
                  }}
                >
                  {p.label}
                </span>
              </div>
              <div
                style={{
                  fontSize: 11,
                  fontWeight: 700,
                  color: isSelected ? "var(--alpha-teal)" : "var(--foreground)",
                  lineHeight: 1.2,
                }}
              >
                {presetCalls.toLocaleString()} call{presetCalls === 1 ? "" : "s"}
              </div>
              <div
                style={{
                  fontSize: 10,
                  color: "var(--muted-foreground)",
                  lineHeight: 1.3,
                  marginTop: 2,
                }}
              >
                {p.blurb}
              </div>
            </button>
          );
        })}
      </div>

      {/* Custom toggle */}
      <button
        onClick={handleCustomToggle}
        className="mt-2 w-full rounded-lg p-2.5 flex items-center justify-between transition-all"
        style={{
          backgroundColor: showCustom ? "var(--alpha-teal-10)" : "var(--card)",
          border: `1px solid ${
            showCustom ? "var(--alpha-teal)" : "var(--border)"
          }`,
          cursor: "pointer",
        }}
      >
        <div className="flex items-center gap-1.5">
          <Settings2
            className="w-3.5 h-3.5"
            style={{ color: showCustom ? "var(--alpha-teal)" : "var(--muted-foreground)" }}
          />
          <span
            style={{
              fontSize: 12,
              fontWeight: 700,
              color: showCustom ? "var(--alpha-teal)" : "var(--foreground)",
            }}
          >
            Custom
          </span>
        </div>
        <span style={{ fontSize: 10, color: "var(--muted-foreground)" }}>
          {showCustom ? "Hide" : "Configure manually"}
        </span>
      </button>

      {/* Custom panel */}
      {showCustom && (
        <div
          className="mt-3 rounded-lg p-3 space-y-3"
          style={{
            backgroundColor: "var(--card)",
            border: "1px solid var(--border)",
          }}
        >
          <NumberControl
            label="Queries"
            help={`Out of ${totalQueries} in the framework`}
            value={value.queries}
            min={1}
            max={totalQueries}
            onChange={(queries) => onChange({ ...value, queries })}
          />
          <NumberControl
            label="Reps per query per model"
            help="How many times each query is sent (variance smoothing)"
            value={value.reps}
            min={1}
            max={10}
            onChange={(reps) => onChange({ ...value, reps })}
          />

          <ScorerPicker
            value={value.scorerModelId}
            onChange={(scorerModelId) => onChange({ ...value, scorerModelId })}
          />

          <ScoringModePicker
            value={value.scoringMode}
            onChange={(scoringMode) => onChange({ ...value, scoringMode })}
          />

          {cost > 10 && (
            <div
              className="flex items-start gap-1.5 p-2 rounded-md"
              style={{
                backgroundColor: "rgba(245,158,11,0.08)",
                border: "1px solid var(--chart-3)",
              }}
            >
              <AlertCircle
                className="w-3 h-3 mt-0.5 flex-shrink-0"
                style={{ color: "var(--chart-3)" }}
              />
              <span
                style={{
                  fontSize: 10,
                  color: "var(--foreground)",
                  lineHeight: 1.4,
                }}
              >
                This scope estimates above $10 — double-check the framework before
                committing.
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * The Stage-5 judge, chosen at Stage 4 — before anything runs.
 *
 * The pipeline scores every collected run automatically with whatever judge is
 * pinned here, so this is the ONE place the judge is chosen. Stage 5 then shows
 * the pass that ran; it no longer asks the question a second time, after the
 * money has been spent.
 */
function ScorerPicker({
  value,
  onChange,
}: {
  value?: string;
  onChange: (id: string | undefined) => void;
}) {
  const [models, setModels] = useState<ScorerModel[]>([]);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    listScorerModels()
      .then((r) => {
        if (!cancelled) setModels(r.models);
      })
      .catch(() => {
        // The registry is unreachable. Say so rather than rendering an empty
        // dropdown that looks like "no judges exist" — the run is still
        // startable and will use the server-side default.
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const defaultModel = models.find((m) => m.is_default);
  const selected = models.find((m) => m.id === value) ?? defaultModel;

  return (
    <div>
      <div className="flex items-center gap-1.5 mb-1">
        <Scale className="w-3 h-3" style={{ color: "var(--signal-blue)" }} />
        <span
          style={{
            fontSize: 10,
            letterSpacing: "0.06em",
            textTransform: "uppercase",
            fontWeight: 700,
            color: "var(--muted-foreground)",
          }}
        >
          Scoring judge
        </span>
      </div>
      <select
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value || undefined)}
        disabled={failed || models.length === 0}
        className="w-full"
        style={{
          fontSize: 12,
          padding: "6px 8px",
          borderRadius: 6,
          border: "1px solid var(--border)",
          backgroundColor: "var(--card)",
          color: "var(--foreground)",
        }}
      >
        <option value="">
          {defaultModel ? `${defaultModel.label} (default)` : "Registry default"}
        </option>
        {models
          .filter((m) => !m.is_default)
          .map((m) => (
            <option key={m.id} value={m.id}>
              {m.label}
            </option>
          ))}
      </select>
      <span
        style={{
          display: "block",
          fontSize: 10,
          color: "var(--muted-foreground)",
          marginTop: 4,
          lineHeight: 1.45,
        }}
      >
        {failed
          ? "Could not load the judge registry — the run will score with the server default."
          : (
            <>
              Scores every response as soon as collection finishes — exactly one
              pass, on this judge.
              {selected ? ` Currently ${selected.model_string}.` : ""} Changing
              the judge after the run has been scored means paying for a second
              full pass.
            </>
          )}
      </span>
    </div>
  );
}

function ScoringModePicker({
  value,
  onChange,
}: {
  value?: ScoringMode;
  onChange: (m: ScoringMode | undefined) => void;
}) {
  // Batch is the default and stays the default: Stage-5 scoring is offline
  // work that nobody waits on, and the discount is 50% of the largest line on
  // the bill. Direct exists for the case where a turnaround genuinely matters
  // — and says what it costs, so that is a decision and not a surprise.
  const OPTIONS: Array<{
    key: ScoringMode;
    label: string;
    blurb: string;
    isDefault?: boolean;
  }> = [
    {
      key: "batch",
      label: "Batch",
      blurb: "half price — results usually within the hour",
      isDefault: true,
    },
    {
      key: "direct",
      label: "Direct",
      blurb: `about ${DIRECT_SCORING_COST_MULTIPLIER}x the cost — scores in minutes`,
    },
  ];
  const selected: ScoringMode = value ?? "batch";

  return (
    <div>
      <div className="flex items-center gap-1.5 mb-1">
        <Layers className="w-3 h-3" style={{ color: "var(--signal-blue)" }} />
        <span
          style={{
            fontSize: 10,
            letterSpacing: "0.06em",
            textTransform: "uppercase",
            fontWeight: 700,
            color: "var(--muted-foreground)",
          }}
        >
          Scoring speed
        </span>
      </div>
      <div className="grid grid-cols-2 gap-2">
        {OPTIONS.map((option) => {
          const active = selected === option.key;
          return (
            <button
              key={option.key}
              type="button"
              onClick={() => onChange(option.isDefault ? undefined : option.key)}
              className="text-left rounded-md p-2 transition-colors"
              style={{
                border: `1px solid ${active ? "var(--signal-blue)" : "var(--border)"}`,
                backgroundColor: active
                  ? "rgba(59,130,246,0.08)"
                  : "var(--card)",
              }}
            >
              <span
                style={{
                  display: "block",
                  fontSize: 12,
                  fontWeight: 600,
                  color: "var(--foreground)",
                }}
              >
                {option.label}
                {option.isDefault ? " (default)" : ""}
              </span>
              <span
                style={{
                  display: "block",
                  fontSize: 10,
                  color: "var(--muted-foreground)",
                  lineHeight: 1.4,
                  marginTop: 2,
                }}
              >
                {option.blurb}
              </span>
            </button>
          );
        })}
      </div>
      <span
        style={{
          display: "block",
          fontSize: 10,
          color: "var(--muted-foreground)",
          marginTop: 4,
          lineHeight: 1.45,
        }}
      >
        Same judge, same rubric, same number of repeats either way — this only
        changes how the judge calls are sent, never what they score. Pick Direct
        when you need the result today and the extra cost is worth it.
      </span>
    </div>
  );
}

function VersionModePicker({
  value,
  onChange,
  pinned,
  hasPins,
}: {
  value: VersionMode;
  onChange: (v: VersionMode) => void;
  // This customer has no baseline of its own, so "Current" is the default and
  // the versions it resolves are pinned to the client for every later read.
  pinned?: boolean;
  // Pins already exist, i.e. this is not the client's first read — "Current"
  // means the versions its first read pinned, not today's catalog.
  hasPins?: boolean;
}) {
  const OPTIONS: Array<{ key: VersionMode; label: string; blurb: string }> = [
    {
      key: "baseline",
      label: "Baseline",
      blurb: pinned
        ? "the IMPORTED baseline's versions — not this client's own"
        : "exact versions the baseline ran with — directly comparable",
    },
    {
      key: "default",
      label: "Current",
      blurb: pinned && hasPins
        ? "the versions this client's first read pinned"
        : pinned
          ? "current versions — pinned to this client from this run on"
          : "current live Upmode model versions",
    },
  ];
  return (
    <div
      className="rounded-lg p-3 mb-2"
      style={{
        backgroundColor: "var(--card)",
        border: "1px solid var(--border)",
      }}
    >
      <div className="flex items-center justify-between mb-1.5">
        <span style={{ fontSize: 11, fontWeight: 600, color: "var(--foreground)" }}>
          Model versions
        </span>
        <span style={{ fontSize: 10, color: "var(--muted-foreground)" }}>
          which versions to run against
        </span>
      </div>
      <div className="grid grid-cols-2 gap-1.5">
        {OPTIONS.map((opt) => {
          const isOn = value === opt.key;
          return (
            <button
              key={opt.key}
              onClick={() => onChange(opt.key)}
              className="text-left rounded-md px-2.5 py-1.5 transition-all"
              style={{
                backgroundColor: isOn ? "var(--alpha-teal-10)" : "var(--muted)",
                border: `1px solid ${isOn ? "var(--alpha-teal)" : "var(--border)"}`,
                cursor: "pointer",
              }}
            >
              <div
                style={{
                  fontSize: 12,
                  fontWeight: 700,
                  color: isOn ? "var(--alpha-teal)" : "var(--foreground)",
                  lineHeight: 1.2,
                }}
              >
                {opt.label}
              </div>
              <div
                style={{
                  fontSize: 10,
                  color: "var(--muted-foreground)",
                  lineHeight: 1.3,
                  marginTop: 2,
                }}
              >
                {opt.blurb}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function LaneModelPicker({
  models,
  mode,
  pins,
  overrides,
  onChange,
  webSearchOverrides,
  onWebSearchChange,
  endpointOverrides,
  onEndpointChange,
}: {
  models: MeasurementModel[];
  mode: VersionMode;
  // The customer's standing per-lane version pins. Not an override the
  // operator set — the version this client is read on (see resolveMeasurementVersion).
  pins: Record<string, string>;
  overrides: Record<string, string>;
  onChange: (next: Record<string, string>) => void;
  webSearchOverrides: Record<string, boolean>;
  onWebSearchChange: (next: Record<string, boolean>) => void;
  endpointOverrides: Record<string, string>;
  onEndpointChange: (next: Record<string, string>) => void;
}) {
  // Only lanes that (a) are selected for this run and (b) have known choices.
  const selected = new Set(models);
  const lanes = MEASUREMENT_LANE_OPTIONS.filter((o) => selected.has(o.lane));
  if (lanes.length === 0) return null;

  const setOverride = (lane: string, value: string) => {
    const next = { ...overrides };
    if (!value) delete next[lane]; // "Auto" → drop the override
    else next[lane] = value;
    onChange(next);
  };

  // Toggle a lane's grounding regime. Keep the map sparse: when the chosen value
  // equals the lane's catalog default, drop the entry (so a plain run records no
  // override); otherwise store the explicit choice.
  const toggleWebSearch = (lane: MeasurementModel, def: boolean) => {
    const current = resolveMeasurementWebSearch(lane, webSearchOverrides);
    const nextVal = !current;
    const next = { ...webSearchOverrides };
    if (nextVal === def) delete next[lane];
    else next[lane] = nextVal;
    onWebSearchChange(next);
  };

  // Toggle a lane's call surface (#90): "sonar" vs "agent". Switching also
  // clears any model_overrides value for that lane — a preset like "medium"
  // is meaningless once flipped back to Sonar, and "sonar-pro" is meaningless
  // once flipped to Agent — so "Auto" reapplies under the new vocabulary.
  //
  // The two writes below land in either order because the parent applies them
  // against previous state, not the render-time value. Do not "simplify" that
  // wiring back to onChange({ ...value, x }) — see BatchConfigPickerProps.
  const toggleEndpoint = (lane: MeasurementModel, def: "sonar" | "agent") => {
    const current = resolveMeasurementEndpoint(lane, endpointOverrides);
    const nextVal: "sonar" | "agent" = current === "sonar" ? "agent" : "sonar";
    const nextEndpoints = { ...endpointOverrides };
    if (nextVal === def) delete nextEndpoints[lane];
    else nextEndpoints[lane] = nextVal;
    const nextOverrides = { ...overrides };
    delete nextOverrides[lane];
    onEndpointChange(nextEndpoints);
    onChange(nextOverrides);
  };

  return (
    <div
      className="rounded-lg p-3 mb-2"
      style={{ backgroundColor: "var(--card)", border: "1px solid var(--border)" }}
    >
      <div className="flex items-center justify-between mb-1.5">
        <span style={{ fontSize: 11, fontWeight: 600, color: "var(--foreground)" }}>
          Model &amp; grounding per provider
        </span>
        <span style={{ fontSize: 10, color: "var(--muted-foreground)" }}>
          exact model + web-grounded vs static
        </span>
      </div>
      <div className="flex flex-col gap-1.5">
        {lanes.map((opt) => {
          const endpoint = resolveMeasurementEndpoint(opt.lane, endpointOverrides);
          const endpointOverridden = Boolean(opt.endpoints) && endpoint !== opt.endpoints!.default;
          const activeChoices = opt.endpoints ? opt.endpoints.choicesByEndpoint[endpoint] : opt.choices;
          const effective = resolveMeasurementVersion(
            opt.lane, mode, overrides, endpointOverrides, pins,
          );
          // What "Auto" resolves to — independent of any currently-selected
          // override, unlike `effective` above. The pins still apply: "Auto" on
          // a pinned client means its pinned version, not the catalog's.
          const autoLabel = resolveMeasurementVersion(
            opt.lane, mode, {}, endpointOverrides, pins,
          );
          const overridden = Boolean(overrides[opt.lane]);
          // Single-choice lanes (e.g. Claude → Sonnet only, or Perplexity on
          // whichever endpoint has just one choice) are display-only.
          const locked = activeChoices.length <= 1;
          const web = resolveMeasurementWebSearch(opt.lane, webSearchOverrides);
          const webOverridden = web !== opt.webSearchDefault;
          return (
            <div
              key={opt.lane}
              className="flex items-center justify-between gap-2"
            >
              <span
                style={{ fontSize: 11, fontWeight: 600, color: "var(--foreground)", flex: 1 }}
              >
                {opt.label}
              </span>
              {locked ? (
                <span
                  style={{
                    fontSize: 11,
                    color: "var(--muted-foreground)",
                    padding: "3px 8px",
                    borderRadius: 6,
                    border: "1px solid var(--border)",
                    backgroundColor: "var(--muted)",
                  }}
                >
                  {getMeasurementVersionLabel(effective)}
                </span>
              ) : (
                <select
                  value={overridden ? overrides[opt.lane] : ""}
                  onChange={(e) => setOverride(opt.lane, e.target.value)}
                  style={{
                    fontSize: 11,
                    fontWeight: 600,
                    padding: "3px 8px",
                    borderRadius: 6,
                    border: `1px solid ${overridden ? "var(--alpha-teal)" : "var(--border)"}`,
                    color: overridden ? "var(--alpha-teal)" : "var(--foreground)",
                    backgroundColor: "var(--muted)",
                    cursor: "pointer",
                  }}
                >
                  <option value="">
                    Auto — {getMeasurementVersionLabel(autoLabel)}
                  </option>
                  {activeChoices.map((c) => (
                    <option key={c.value} value={c.value}>
                      {c.label}
                    </option>
                  ))}
                </select>
              )}
              {/* Endpoint toggle (#90): which call surface this lane uses —
                  today only Perplexity has more than one (Sonar vs Agent). */}
              {opt.endpoints && (
                <button
                  type="button"
                  onClick={() => toggleEndpoint(opt.lane, opt.endpoints!.default)}
                  title={
                    endpointOverridden
                      ? `Overridden from default (${opt.endpoints!.default === "agent" ? "Agent" : "Sonar"})`
                      : "Catalog default"
                  }
                  style={{
                    fontSize: 11,
                    fontWeight: 600,
                    padding: "3px 8px",
                    borderRadius: 6,
                    minWidth: 58,
                    textAlign: "center",
                    border: `1px solid ${endpointOverridden ? "var(--alpha-teal)" : "var(--border)"}`,
                    color: endpointOverridden ? "var(--alpha-teal)" : "var(--foreground)",
                    backgroundColor: "var(--muted)",
                    cursor: "pointer",
                  }}
                >
                  {endpoint === "agent" ? "Agent" : "Sonar"}
                </button>
              )}
              {/* Grounding toggle: Web (search ON) vs Static (parametric only). */}
              <button
                type="button"
                onClick={() => toggleWebSearch(opt.lane, opt.webSearchDefault)}
                title={
                  webOverridden
                    ? `Overridden from default (${opt.webSearchDefault ? "Web" : "Static"})`
                    : "Catalog default"
                }
                style={{
                  fontSize: 11,
                  fontWeight: 600,
                  padding: "3px 8px",
                  borderRadius: 6,
                  minWidth: 58,
                  textAlign: "center",
                  border: `1px solid ${webOverridden ? "var(--alpha-teal)" : "var(--border)"}`,
                  color: webOverridden
                    ? "var(--alpha-teal)"
                    : web ? "var(--foreground)" : "var(--muted-foreground)",
                  backgroundColor: "var(--muted)",
                  cursor: "pointer",
                }}
              >
                {web ? "Web" : "Static"}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function NumberControl({
  label,
  help,
  value,
  min,
  max,
  onChange,
}: {
  label: string;
  help?: string;
  value: number;
  min: number;
  max: number;
  onChange: (v: number) => void;
}) {
  const clamp = (v: number) => Math.max(min, Math.min(max, v));
  return (
    <div>
      <div className="flex items-center justify-between mb-1.5">
        <div>
          <div
            style={{
              fontSize: 11,
              fontWeight: 600,
              color: "var(--foreground)",
            }}
          >
            {label}
          </div>
          {help && (
            <div style={{ fontSize: 10, color: "var(--muted-foreground)" }}>
              {help}
            </div>
          )}
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={() => onChange(clamp(value - 1))}
            className="w-6 h-6 rounded flex items-center justify-center"
            style={{
              backgroundColor: "var(--muted)",
              border: "1px solid var(--border)",
              color: "var(--foreground)",
              cursor: "pointer",
              fontSize: 14,
              fontWeight: 700,
              lineHeight: 1,
            }}
          >
            −
          </button>
          <input
            type="number"
            value={value}
            min={min}
            max={max}
            onChange={(e) => onChange(clamp(Number(e.target.value) || min))}
            style={{
              width: 48,
              padding: "3px 6px",
              fontSize: 12,
              fontWeight: 600,
              textAlign: "center",
              backgroundColor: "var(--card)",
              border: "1px solid var(--border)",
              borderRadius: 4,
              color: "var(--foreground)",
              outline: "none",
            }}
          />
          <button
            onClick={() => onChange(clamp(value + 1))}
            className="w-6 h-6 rounded flex items-center justify-center"
            style={{
              backgroundColor: "var(--muted)",
              border: "1px solid var(--border)",
              color: "var(--foreground)",
              cursor: "pointer",
              fontSize: 14,
              fontWeight: 700,
              lineHeight: 1,
            }}
          >
            +
          </button>
        </div>
      </div>
    </div>
  );
}